<script lang="ts" module>
	import Header from '#blocks/header.svelte'
	import LinkList from '#blocks/linklist.svelte'
	import { Storyblok } from '#library/storyblok.svelte.ts'
	import Render from '#components/render.svelte'
	import type { ComponentProps } from 'svelte'
	import Share from '#components/share.svelte'
	import type { ISbStories } from '@storyblok/svelte'
	import type { Gallery } from '#library/components.schema.build.ts'
	import type { Gallery as GalleryTemplate } from '#library/types.ts'
	import Featured from '#blocks/featured.svelte'
	import Image from '#components/image.svelte'
	import Modal from '#components/modal.svelte'
	import emblaCarouselSvelte from 'embla-carousel-svelte'
	import type { EmblaCarouselType } from 'embla-carousel'
	import { addThumbBtnsClickHandlers, addToggleThumbBtnsActive } from '#library/emblaThumbs.ts'

	// results go on the story, not module scope: module state is shared by every
	// request the server process handles, so one visitor's related galleries were
	// served to everyone else. `story` is per-request, so writing there is safe.
	export const load: Load<GalleryTemplate> = async ({ story, api, version }) => {
		/** Fallback to retrieve 3 other stories (with blog/news type) */
		const {
			data: { stories }
		} = (await api.get('cdn/stories', {
			version,
			content_type: story.content.component,
			per_page: 3,
			resolve_links: 'url',
			excluding_slugs: story.full_slug,
			sort_by: 'first_published_at:desc'
		})) as ISbStories<Gallery>

		story.content.more = stories
	}

	interface Props {
		data: Storyblok<GalleryTemplate>
	}
</script>

<script lang="ts">
	let { data }: Props = $props()

	const { content } = $derived(data.story ?? { content: null })

	let open: boolean | number = $state(false)
	let slides: EmblaCarouselType | undefined = $state()
	let thumbs: EmblaCarouselType | undefined = $state()

	$effect(() => {
		if (!slides || !thumbs) return
		if (typeof open === 'number') slides.scrollTo(open - 1)
		if (typeof open === 'number') thumbs.scrollTo(open - 1)
		const removeThumbBtnsClickHandlers = addThumbBtnsClickHandlers(slides, thumbs)
		const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(slides, thumbs)
		slides.on('destroy', removeThumbBtnsClickHandlers).on('destroy', removeToggleThumbBtnsActive)
		thumbs.on('destroy', removeThumbBtnsClickHandlers).on('destroy', removeToggleThumbBtnsActive)
	})
</script>

<article class="article">
	<Header blok={{ accent: 'true' }} />

	<div class="contain">
		{#if content?.body}
			<div class="body">
				<Render content={content.body} />
			</div>
		{/if}
		<div class="images">
			{#each content?.images as image, i (image.id)}
				{#if image.filename}
					<button class="item" onclick={() => (open = i + 1)}>
						<Image src={image.filename} alt={image.alt} focus={image.focus} aspect="1/1" />
					</button>
				{/if}
			{/each}
		</div>
	</div>

	<Share />
</article>

{#if open !== false}
	<Modal title="Gallery {open}" close={() => (open = false)} {open}>
		<div class="carousel">
			<div class="viewport" use:emblaCarouselSvelte onemblaInit={e => (slides = e.detail)}>
				<div class="slides">
					{#each content?.images as image (image.id)}
						{#if image.filename}
							<div class="slide">
								<Image src={image.filename} alt={image.alt} focus={image.focus} />
							</div>
						{/if}
					{/each}
				</div>
			</div>
			<div class="thumbnails">
				<div
					class="viewport"
					use:emblaCarouselSvelte={{ options: { containScroll: 'keepSnaps', dragFree: true }, plugins: [] }}
					onemblaInit={e => (thumbs = e.detail)}
				>
					<div class="thumbs">
						{#each content?.images as image (image.id)}
							{#if image.filename}
								<Image src={image.filename} alt={image.alt} focus={image.focus} />
							{/if}
						{/each}
					</div>
				</div>
			</div>
		</div>
	</Modal>
{/if}

{#if content?.more}
	<Featured
		blok={{
			stories: content.more,
			theme: 'theme-crocus-on-gimson',
			title: 'More galleries'
		}}
	/>
{/if}

<LinkList
	blok={{
		theme: 'theme-rose-on-white',
		title: 'Discover what we stand for',
		body: 'Bedales champions an innovative student-centred education, prioritising academic freedom, creativity, sustainability and wellbeing. We foster individuality, moral purpose and a lifelong love of learning in a changing world.',
		items: [
			{
				link: {
					text: 'Learning',
					url: '/learning'
				}
			},
			{
				link: {
					text: 'Living',
					url: '/living'
				}
			},
			{
				link: {
					text: 'Community',
					url: '/community'
				}
			},
			{
				link: {
					text: 'Environment',
					url: '/environment'
				}
			}
		]
	} as unknown as ComponentProps<typeof LinkList>['blok']}
/>

<style lang="css">
	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-start: var(--s-80);
		max-width: 70ch;
	}

	.images {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 1rem;
		padding-block: var(--s-32);
	}

	.item {
		overflow: clip;

		:global(.image) {
			transition: scale 0.3s ease;

			&:hover {
				scale: 1.1;
			}
		}

		&:nth-child(18n + 1),
		&:nth-child(18n + 12) {
			grid-column: span 2;
			grid-row: span 2;
		}
	}

	.carousel {
		display: grid;
		gap: 2rem;
		grid-column: 1 / -1;
		grid-template-rows: 1fr 6rem;
		height: 70vh;
		margin-inline: -2rem;
		cursor: grab;
		user-select: none;
	}

	.viewport {
		height: 100%;
		overflow: hidden;
	}

	.slides {
		display: flex;
		height: 100%;
		margin-block-end: 2rem;

		:global(.image) {
			inset: 0;
			object-fit: contain;
			position: absolute;
			size: 100%;
		}
	}

	.slide {
		flex: 1 0 calc(100vw - 2.75rem);
		margin-inline: 2rem;

		@media (--from-tablet) {
			flex: 1 0 calc(100% - 4rem);
		}
	}

	.thumbs {
		display: flex;
		gap: 1rem;
		padding-inline: 2rem;

		:global(.image) {
			cursor: pointer;
			height: 6rem;
		}
	}

	.thumb {
	}
</style>
